
import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { RevenueSplit } from '@/hooks/license/resellerPlans/types';
import RevenueSplitHeader from './revenue/RevenueSplitHeader';
import NewRevenueSplitForm from './revenue/NewRevenueSplitForm';
import RevenueSplitTable from './revenue/RevenueSplitTable';

interface RevenueSplitManagementProps {
  revenueSplits: RevenueSplit[];
  onAddRevenueSplit: (revenueSplit: Partial<RevenueSplit>) => void;
  onUpdateRevenueSplit: (id: string, updates: Partial<RevenueSplit>) => void;
  onDeleteRevenueSplit: (id: string) => void;
  onSaveRevenueSplits: () => Promise<boolean>;
  isSaving: boolean;
}

const RevenueSplitManagement: React.FC<RevenueSplitManagementProps> = ({
  revenueSplits,
  onAddRevenueSplit,
  onUpdateRevenueSplit,
  onDeleteRevenueSplit,
  onSaveRevenueSplits,
  isSaving
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newSplit, setNewSplit] = useState({
    volumeThreshold: 0,
    masterLicensePercentage: 70,
    resellerPercentage: 30,
    isActive: true
  });

  const handleUpdateMasterPercentage = (id: string, value: number) => {
    if (value < 0 || value > 100) return;
    onUpdateRevenueSplit(id, {
      masterLicensePercentage: value,
      resellerPercentage: 100 - value
    });
  };

  const handleUpdateResellerPercentage = (id: string, value: number) => {
    if (value < 0 || value > 100) return;
    onUpdateRevenueSplit(id, {
      resellerPercentage: value,
      masterLicensePercentage: 100 - value
    });
  };

  const handleNewMasterPercentageChange = (value: number) => {
    if (value < 0 || value > 100) return;
    setNewSplit({
      ...newSplit,
      masterLicensePercentage: value,
      resellerPercentage: 100 - value
    });
  };

  const handleNewResellerPercentageChange = (value: number) => {
    if (value < 0 || value > 100) return;
    setNewSplit({
      ...newSplit,
      resellerPercentage: value,
      masterLicensePercentage: 100 - value
    });
  };

  const handleAddNew = () => {
    onAddRevenueSplit(newSplit);
    setNewSplit({
      volumeThreshold: 0,
      masterLicensePercentage: 70,
      resellerPercentage: 30,
      isActive: true
    });
    setIsAdding(false);
  };

  const handleSave = async () => {
    try {
      const result = await onSaveRevenueSplits();
      if (!result) {
        console.error("Failed to save revenue splits");
      }
    } catch (error) {
      console.error("Error saving revenue splits:", error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <RevenueSplitHeader 
          setIsAdding={setIsAdding}
          isAdding={isAdding}
          handleSave={handleSave}
          isSaving={isSaving}
        />
      </CardHeader>
      <CardContent>
        {isAdding && (
          <NewRevenueSplitForm
            newSplit={newSplit}
            setNewSplit={setNewSplit}
            handleNewMasterPercentageChange={handleNewMasterPercentageChange}
            handleNewResellerPercentageChange={handleNewResellerPercentageChange}
            handleAddNew={handleAddNew}
            setIsAdding={setIsAdding}
          />
        )}

        <RevenueSplitTable
          revenueSplits={revenueSplits}
          handleUpdateMasterPercentage={handleUpdateMasterPercentage}
          handleUpdateResellerPercentage={handleUpdateResellerPercentage}
          onUpdateRevenueSplit={onUpdateRevenueSplit}
          onDeleteRevenueSplit={onDeleteRevenueSplit}
        />
      </CardContent>
    </Card>
  );
};

export default RevenueSplitManagement;
